import React, { useContext, useState, useEffect } from 'react';
import { AppContext } from '@prisjakt/ui-app-context';
import { ViewportContext } from '../../../../../components/viewport';

import { CardContent, CardMedia, CardActionArea } from '@prisjakt/ui/card';
import { RowCard } from '@prisjakt/ui/card/variants';
import Clickable from '@prisjakt/ui-clickable';

import * as S from './styles';
import { Subtitle } from '../../../styles';

import {
  trackExpertToplistsSummaryViewed,
  trackExpertToplistsSummaryExpanded,
  trackExpertToplistsSummaryClicked,
} from '../../../tracking';

import { AdvizedToplist } from '../../../../../types';

type Props = {
  toplists: AdvizedToplist[][];
};

const LIMIT_DESKTOP = 5;
const LIMIT_MOBILE = 2;

const positionsInList = (toplist: AdvizedToplist[]) => {
  // Returns a comma separated list with the position of all products
  // in a list, and the last item in the list separated with "&".

  const positions = toplist.map(l => l.products.map(p => p.order)).flat();

  if (positions.length === 1) {
    return positions[0].toString();
  }

  return `${positions.slice(0, positions.length - 1).join(', ')} & ${
    positions[positions.length - 1]
  }`;
};

function ListOfToplists({ toplists }: Props) {
  const [expanded, setExpanded] = useState(false);
  const { t } = useContext(AppContext);
  const { isDesktop } = useContext(ViewportContext);

  useEffect(() => {
    trackExpertToplistsSummaryViewed();
  }, []);

  const handleExpand = () => {
    if (!expanded) {
      trackExpertToplistsSummaryExpanded();
    }

    setExpanded(state => !state);
  };

  const limit = isDesktop ? LIMIT_DESKTOP : LIMIT_MOBILE;

  const CardStyling = {
    display: 'grid',
    gridTemplateColumns: '1fr 3fr',
    width: '100%',
  };

  return (
    <S.Wrapper>
      {toplists
        .slice(0, !expanded ? limit : toplists.length)
        .map((toplist, index) => {
          const list = toplist[0];
          const image = list.description?.find(d => d.type === 'image');
          const positions = positionsInList(toplist);

          if (!image?.data) {
            return null;
          }

          return (
            <RowCard key={list.title}>
              <CardActionArea
                style={CardStyling}
                container={Clickable}
                containerProps={{
                  // @ts-ignore
                  to: list.headlineInfo.url,
                  onClick: () => {
                    trackExpertToplistsSummaryClicked({
                      title: list.title,
                      order: index,
                    });
                  },
                }}
              >
                <CardMedia style={{ height: '100%' }}>
                  <S.Image alt="" src={image.data} />
                </CardMedia>

                <CardContent>
                  {toplist.length > 1 && (
                    <S.ExpertCount>
                      {t('expert', 'guide.lists-expert-plural', {
                        count: toplist.length,
                      })}
                    </S.ExpertCount>
                  )}

                  <Subtitle>{list.title}</Subtitle>

                  <S.Position>
                    {t('expert', 'toplist.pos-in-n-lists', {
                      n: toplist.length,
                      pos: positions,
                    })}
                  </S.Position>
                </CardContent>
              </CardActionArea>
            </RowCard>
          );
        })}

      {toplists.length > limit && (
        <S.ExpandButton color="primary" variant="text" onClick={handleExpand}>
          {expanded
            ? t('expert', 'toplist.view-less-toplists')
            : t('expert', 'toplist.view-all-toplists', {
                count: toplists.length,
              })}
        </S.ExpandButton>
      )}
    </S.Wrapper>
  );
}

export default ListOfToplists;
